import React from "react";
import { Layout, Row, Col, Space, Anchor } from "antd";
import "./App.less";
import "@dynata/axiom3/axiom.css";
import ComponentsNav from "./layout/componentsNav";
import { AxButton, AxDivider } from "@dynata/axiom3";
import Typography from "./components/typography";
import Colors from "./components/colors";
import Accordion from "./components/accordion";
import Button from "./components/button";
import IconWrapper from "./components/icon-wrapper";
import Alert from "./components/alert";
import Artwork from "./components/artwork";
import AutoComplete from "./components/autocomplete";
import Avatar from "./components/avatar";
import Badge from "./components/badge";
import Banner from "./components/banner";
import Breadcrumb from "./components/breadcrumb";
import Card from "./components/card";
import Calendar from "./components/calendar";
import Checkbox from "./components/checkbox";
import Comment from "./components/comment";
import DatePicker from "./components/datepicker";
import DividerEx from "./components/divider";
import Dropdown from "./components/dropdown";
// import Flag from "./components/flag";
import Input from "./components/input";
import InputNumber from "./components/input-number";
import Message from "./components/message";
import Modal from "./components/modal";
import Notification from "./components/notification";
import ProgrammingNotes from "./components/notes";
import Pagination from "./components/pagination";
import Password from "./components/password";
import PopConfirm from "./components/popconfirm";
import PopOver from "./components/popover";
import Progress from "./components/progress";
import Radio from "./components/radio";
import Rating from "./components/rating";
import Select from "./components/select";
import Search from "./components/search";
import Skeleton from "./components/skeleton";
import Slider from "./components/slider";
import Spinner from "./components/spinner";
import Stats from "./components/stats";
import Steps from "./components/steps";
import Switch from "./components/switch";
import Table from "./components/table";
import Tags from "./components/tags";
import Textarea from "./components/textarea";
import Timepicker from "./components/timepicker";
import Tooltip from "./components/tooltip";
import Tree from "./components/tree";
import TreeSelect from "./components/treeselect";
import Upload from "./components/upload";
import NavHeader from "./components/header";
import SideMenu from "./components/sidemenu";

const { Header, Content, Sider } = Layout;
const { Link } = Anchor;

class App extends React.Component {
  render() {
    return (
      <div className="App">
        <Layout>
          <Header style={{ background: "rgb(241, 242, 243)" }}>
            <div className="logo body semibold l">Axiom3 Documentation</div>
            <Space size={12}>
              <AxButton
                color="main"
                target="_blank"
                href="https://axiom3-dynata.web.app/"
              >
                Storybook
              </AxButton>

              <AxButton color="main" target="_blank" href="https://ant.design/">
                AntDesign
              </AxButton>
              <AxButton
                color="main"
                target="_blank"
                href="https://github.com/dynata/axiom3/packages/"
              >
                GitHub
              </AxButton>
            </Space>
          </Header>
          <Layout>
            <Sider theme="light" width={250}>
              <Anchor>
                <Link className="main-cat" href="#intro" title="Introduction" />
                <Link
                  className="main-cat"
                  href="#components"
                  title="Components"
                >
                  <ComponentsNav />
                </Link>
              </Anchor>
            </Sider>
            <Content>
              <Row type="flex" align="middle" justify="center">
                <Col span={20}>
                  <div className="section" id="intro">
                    <h3>Introduction</h3>
                    <Space direction="vertical">
                      <p>
                        Axiom Design System is essentially a UI component
                        library for ReactJS applications. It is built on top of
                        AntDesign where each Axiom component is extended from
                        one or more AntDesign components. Axiom components share
                        most of the props from AntDesign along with some extra
                        props of their own, which are defined in detail in their
                        respective sections, below.
                      </p>
                      <h4>Installing Axiom</h4>
                      <p>
                        To add axiom in your project, use the following steps:
                      </p>
                      <p>
                        Create personal access token on&nbsp;
                        <AxButton
                          color="blue"
                          type="link"
                          target="_blank"
                          href="https://docs.github.com/en/github/authenticating-to-github/keeping-your-account-and-data-secure/creating-a-personal-access-token"
                        >
                          Github
                        </AxButton>
                        &nbsp;and add `.npmrc` file with the following
                        configuration in your project:
                      </p>

                      <code>
                        {`//npm.pkg.github.com/:_authToken=\${GITHUB_DYNATA_AUTH_TOKEN}
@dynata:registry=https://npm.pkg.github.com`}
                      </code>

                      <p>
                        Set environment variable, GITHUB_DYNATA_AUTH_TOKEN, then
                        install axiom using npm or yarn:
                      </p>
                      <code>npm install @dynata/axiom3</code>
                      <code>yarn add @dynata/axiom3</code>

                      <p>
                        In your react app, add the following in the &lt;head&gt;
                        section of index.html to include icons.
                      </p>
                      <code>
                        {`<script src="https://kit.fontawesome.com/e6650f910d.js" crossorigin="anonymous"></script>`}
                      </code>

                      <br />
                      <h4>Basic Usage</h4>
                      <p>
                        In the subsequent sections, each component and its usage
                        is defined but essentially it’s just a three step
                        process.
                      </p>
                      <ul>
                        <li>
                          Import axiom styles in App.js or any top level
                          container.
                          <br />
                          e.g:{" "}
                          <code className="inline">
                            {'import "@dynata/axiom3/axiom.css";'}
                          </code>
                        </li>
                        <li>
                          Import the UI component from Axiom into your own React
                          component.
                          <br />
                          e.g:{" "}
                          <code className="inline">
                            {'import { AxButton } from "@dynata/axiom3";'}
                          </code>
                        </li>
                        <li>
                          Render the component and provide appropriate props.
                          <br />
                          e.g:{" "}
                          <code className="inline">
                            {'<AxButton type="primary">Ok</AxButton>'}
                          </code>
                        </li>
                      </ul>
                      <p>
                        Where, type="primary" is one of the many props in axiom
                        component. All axiom component begin with the prefix
                        “Ax” and each component has its own particular set of
                        props, which are stated in the upcoming sections.
                      </p>
                      <h4>Resources</h4>
                      <div
                        style={{
                          display: "flex",
                          gap: "12px",
                        }}
                      >
                        <AxButton
                          href="https://github.com/dynata/axiom3"
                          target="_blank"
                        >
                          Github Repo
                        </AxButton>
                        <AxButton
                          href="https://axiom3-dynata.web.app/"
                          target="_blank"
                        >
                          Storybook
                        </AxButton>
                        <AxButton
                          href="https://ant.design/components/overview/"
                          target="_blank"
                        >
                          AntDesign
                        </AxButton>
                        <AxButton
                          href="https://github.com/dynata/axiom3-antd-tokens"
                          target="_blank"
                        >
                          Design Tokens
                        </AxButton>
                      </div>
                      <br />
                      <p className="body regular s">
                        For feedback and support, please reach out to{" "}
                        <AxButton
                          type="link"
                          size="small"
                          href="mailto:saad.khan@dynata.com"
                        >
                          Saad Khan
                        </AxButton>{" "}
                        or{" "}
                        <AxButton
                          type="link"
                          size="small"
                          href="mailto:maaz.nisar@dynata.com"
                        >
                          Maaz Nisar
                        </AxButton>
                        .
                      </p>
                    </Space>
                  </div>

                  <AxDivider />

                  <Colors />

                  <AxDivider />

                  <Typography />

                  <AxDivider />

                  <IconWrapper />

                  <AxDivider />

                  <Accordion />

                  <AxDivider />

                  <Alert />

                  <AxDivider />

                  <Artwork />

                  <AxDivider />

                  <AutoComplete />

                  <AxDivider />

                  <Avatar />

                  <AxDivider />

                  <Badge />

                  <AxDivider />

                  <Banner />

                  <AxDivider />

                  <Breadcrumb />

                  <AxDivider />

                  <Button />

                  <AxDivider />

                  <Card />

                  <AxDivider />

                  <Calendar />

                  <AxDivider />

                  <Checkbox />

                  <AxDivider />

                  <Comment />

                  <AxDivider />

                  <DatePicker />

                  <AxDivider />

                  <DividerEx />

                  <AxDivider />

                  <Dropdown />

                  {/* <AxDivider />

                  <Flag /> */}

                  <AxDivider />

                  <NavHeader />

                  <AxDivider />

                  <Input />

                  <AxDivider />

                  <InputNumber />

                  <AxDivider />

                  <Message />

                  <AxDivider />

                  <Modal />

                  <AxDivider />

                  <Notification />

                  <AxDivider />

                  <ProgrammingNotes />

                  <AxDivider />

                  <Pagination />

                  <AxDivider />

                  <Password />

                  <AxDivider />

                  <PopConfirm />

                  <AxDivider />

                  <PopOver />

                  <AxDivider />

                  <Progress />

                  <AxDivider />

                  <Radio />

                  <AxDivider />

                  <Rating />

                  <AxDivider />

                  <Select />

                  <AxDivider />

                  <Search />

                  <AxDivider />

                  <SideMenu />

                  <AxDivider />

                  <Skeleton />

                  <AxDivider />

                  <Slider />

                  <AxDivider />

                  <Spinner />

                  <AxDivider />

                  <Stats />

                  <AxDivider />

                  <Steps />

                  <AxDivider />

                  <Switch />

                  <AxDivider />

                  <Table />

                  <AxDivider />

                  <Tags />

                  <AxDivider />

                  <Textarea />

                  <AxDivider />

                  <Timepicker />

                  <AxDivider />

                  <Tooltip />

                  <AxDivider />

                  <Tree />

                  <AxDivider />

                  <TreeSelect />

                  <AxDivider />

                  <Upload />
                </Col>
              </Row>
            </Content>
          </Layout>
        </Layout>
      </div>
    );
  }
}

export default App;
